<template>
  <div class="search-object-list">
    <search-object-list-loading v-show="llMixin_componentIsLoading" />
    <div v-show="!llMixin_componentIsLoading">
      <search-capacity />
      <div
        v-show="showMissingDate"
        class="alert alert-warning"
      >
        <i class="icon"><icon-calender /></i>
        {{ $t('search:showMissingDate') }}
      </div>
      <div class="search-list-header">
        <h2
          v-if="criteriaMatchSearch"
          v-dompurify-html="$t('search:numberOfAccommodationsCriteriaMatch', {
            total: totalObjects,
            criteriaMatch: totalCountCriteriaMatch,
            destination: destination
          })"
          class="search-list-header-amount"
        />
        <h2
          v-if="totalObjectsCollapseCount > 0"
          v-dompurify-html="$t('search:numberOfAccommodationsCollapse', {
            total: totalObjects,
            collapse: totalObjectsCollapse,
            destination: destination
          })"
          class="search-list-header-amount"
        />
        <h2
          v-else
          v-dompurify-html="$t('search:numberOfAccommodations', {
            total: totalObjects,
            destination: destination
          })"
          class="search-list-header-amount"
        />
        <div
          class="search-list-header-sort"
        >
          <sort-dropdown
            v-model="sort"
            :sort-values="sortDropdownOptions"
            :displayed-word="setDisplayedWord"
            translation="common:sortWords"
          />
        </div>
      </div>
      <div v-show="hasObjects && !stoprender">
        <!-- client search with intersection observer -->
        <template v-if="$store.state.search.clientSearch">
          <div v-show="false && wasCoordinatesSearch">
            <search-list-alert />
          </div>
          <template
            v-for="(SearchObject, searchIndex) in objects"
          >
            <component-observer
              :key="SearchObject.objectNumber"
              :root-margin="['xs','sm'].includes(breakpoint) ? '2000px' : '1000px'"
              :transition-prop="{ name: 'no-transition' }"
            >
              <template #loading-content>
                <div
                  :style="{height: ['xs','sm'].includes(breakpoint) ? '500px' : '250px',
                           width: '100%',
                           'border-radius': '10px',
                           'background-color': '#e1e1e1',
                           display: 'flex',
                           'align-items': 'center',
                           'justify-content': 'center',
                           'margin-bottom': '15px'}"
                />
              </template>
              <div
                v-if="criteriaMatchSearch
                  && SearchObject.listOrderNumber === Number(totalCountCriteriaMatch) + 1"
                :key="searchIndex + '_alert'"
                class="alert alert-primary"
              >
                <i class="icon">
                  <icon-info-circle-alternative />
                </i>
                {{ $t('search:notMatchingObjects') }}
              </div>

              <search-object
                v-observe-visibility="{
                  callback: (isVisible, entry) => visibilityChanged(isVisible, entry, SearchObject.objectNumber),
                  once: true,
                  throttle: 1500,
                }"
                :search-object="SearchObject"
                :search-start-date="searchStartDate"
              />
            </component-observer>
            <search-object-list-pagination-sentinel
              v-if="searchIndex === 9"
              :key="searchIndex"
            />
          </template>
        </template>
        <!-- server search with lazy hydration -->
        <template v-else>
          <div v-show="wasCoordinatesSearch">
            <search-list-alert />
          </div>
          <template
            v-for="(SearchObject, searchIndex) in objects"
          >
            <div
              v-if="criteriaMatchSearch
                && SearchObject.listOrderNumber === Number(totalCountCriteriaMatch) + 1"
              :key="searchIndex + '_alert'"
              class="alert alert-primary"
            >
              <i class="icon">
                <icon-info-circle-alternative />
              </i>
              {{ $t('search:notMatchingObjects') }}
            </div>
            <lazy-hydrate
              :key="SearchObject.objectNumber"
              ref="searchObjects"
              :when-visible="{ rootMargin: '500px' }"
              :trigger-hydration="$store.state.search.clientSearch"
            >
              <search-object
                v-observe-visibility="{
                  callback: (isVisible, entry) => visibilityChanged(isVisible, entry, SearchObject.objectNumber),
                  once: true,
                  throttle: 1500,
                }"
                :search-object="SearchObject"
                :search-start-date="searchStartDate"
              />
            </lazy-hydrate>
            <search-object-list-pagination-sentinel
              v-if="searchIndex === 9"
              :key="searchIndex"
            />
          </template>
        </template>
      </div>
      <!-- client search with intersection observer -->
      <search-empty-result v-if="!hasObjects" />
    </div>
  </div>
</template>

<script>
import LazyHydrate from 'vue-lazy-hydration';
import SearchObject from 'components/SearchObject.vue';
import SearchCapacity from 'components/SearchCapacity.vue';
import SortDropdown from 'components/SortDropdown.vue';
import SearchObjectListLoading from 'components/loading/SearchObjectListLoading.vue';
import SearchObjectListPaginationSentinel from 'components/SearchObjectListPaginationSentinel.vue';
import SearchEmptyResult from 'components/SearchEmptyResult.vue';
import SearchListAlert from 'components/SearchListAlert.vue';
import LoadingLogic from 'mixins/LoadingLogic';
import searchRedirect from 'mixins/composition/search/SearchFunctions';
import getNumberFormat from 'mixins/composition/NumberFormat';
import AnchorRouterPush from 'mixins/AnchorRouterPush';
import UserApi from 'mixins/UserApi';
import { fromAPIDateString, toDateString } from 'utils/DateHelper';
import { onMounted, onUnmounted } from '@vue/composition-api';
import { breakpoint, setupMediaQueries, killMediaListeners } from 'mixins/composition/DeviceHelper';
import hydrationPreventionSetup from 'mixins/composition/search/HeaderSearchHydrationPrevention';
import { ObserveVisibility } from 'vue-observe-visibility';
import { trackSearchObject } from 'requests/searchObjects';
import IconInfoCircleAlternative from 'ICON:IconInfoCircleAlternative';
import IconCalender from 'ICON:IconCalender';
import {
  computed,
} from '@/mixins/SearchFilter';

/** Icons */

export default {
  name: 'SearchObjectList',
  components: {
    LazyHydrate,
    ComponentObserver: () => import(/* webpackChunkName: "component-observer" */ 'components/loading/ComponentObserver.vue'),
    SearchObject,
    SearchCapacity,
    SortDropdown,
    SearchObjectListLoading,
    SearchObjectListPaginationSentinel,
    SearchEmptyResult,
    SearchListAlert,
    IconInfoCircleAlternative,
    IconCalender,
  },
  directives: {
    'observe-visibility': ObserveVisibility,
  },
  mixins: [LoadingLogic, AnchorRouterPush, UserApi, computed],
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
    stoprender: {
      type: Boolean,
      default: false,
    },
    isSeoPage: {
      type: Boolean,
      default: false,
    },
  },
  setup(_, ctx) {
    const { dispatchMountSignal } = hydrationPreventionSetup(ctx.root.$store, ctx.root, ctx.refs);
    onMounted(() => { setupMediaQueries(); dispatchMountSignal('searchObjectList'); });
    onUnmounted(() => { killMediaListeners(); });
    const { formatNumber } = getNumberFormat(ctx);
    return {
      searchRedirect,
      formatNumber,
      breakpoint,
    };
  },
  data() {
    return {
      resultCount: 1,
      modalReady: false,
    };
  },
  computed: {
    sortDropdownOptions() {
      const orderValues = ['rating', 'priceAsc', 'priceDesc', 'recommendation'];
      if (this.UserApi_checkPermission('searchNewOAI.show')) {
        orderValues.push('newOAIv2');
        orderValues.push('newOAIv5');
        orderValues.push('newOAIv6');
      }
      /*
      if (this.UserApi_checkPermission('search.useLabels')) {
        if (this.$store.getters.getPartnerConfigValue('addPartnerTagsOrder') === 'Y' && this.$store.getters.getPartnerConfigValue('partnerTagName') !== '') {
          orderValues.push('partnerTags');
        }
      }
      */
      return orderValues;
    },
    objectsPerPage() {
      return this.$store.state.searchFilter.objectsPerPage;
    },
    wasCoordinatesSearch() {
      return this.$store.getters.checkCoordinatesSearch;
    },
    partnerTagName() {
      return this.$store.getters.getPartnerConfigValue('partnerTagName');
    },
    setDisplayedWord() {
      // switch to set the displayed value of the dropdown
      return this.$t(`common:sortWords.${((this.sort === 'partnerTags') ? `partnerTags.${this.partnerTagName}` : this.sort)}`);
    },
    searchStartDate() {
      const startDate = this.$store.getters.getSearchFilter('startDate') || '';
      if (startDate) {
        const startDateStringSave = toDateString(fromAPIDateString(startDate));
        return startDateStringSave.toString();
      }
      return '';
    },
    sort: {
      get() {
        return this.$store.getters.getSearchFilter('sort') || 'recommendation';
      },
      set(newSort) {
        searchRedirect({ router: this.$router, filters: { sort: newSort } });
      },
    },
    hasObjects() {
      return Object.keys(this.$store.getters.getSearchObjects).length > 0;
    },
    objects() {
      return this.$store.getters.getSearchObjects;
    },
    totalObjects() {
      const resultCount = this.$store.state.searchTotalObjects;
      // this.setPagination();
      // added option to disable seperators incase this isnt wished anymore
      return this.formatNumber(resultCount, undefined, { useGrouping: true });
    },
    totalObjectsCollapseCount() {
      return this.$store.state.searchTotalObjectsCollapse;
    },
    totalObjectsCollapse() {
      // added option to disable seperators incase this isnt wished anymore
      return this.formatNumber(this.totalObjectsCollapseCount, undefined, { useGrouping: true });
    },
    destination() {
      const { locationInformations } = this.$store.state;
      if (locationInformations.locationNameWithPreposition && locationInformations.locationNameWithPreposition !== '') {
        return locationInformations.locationNameWithPreposition;
      }
      return `${locationInformations.locationNamePreposition} ${locationInformations.locationName}`;
    },
    totalCountCriteriaMatch() {
      const resultCount = this.$store.state.searchTotalObjectsCriteriaMatch;
      // this.setPagination();
      return this.formatNumber(resultCount, undefined, { useGrouping: true });
    },
    criteriaMatchSearch() {
      return this.$store.state.criteriaMatchSearch || false;
    },
    capacity() {
      return this.$store.state.searchCapacity;
    },
  },
  created() {
    this.llMixin_addComponentLoadingIndicator('isLoading');
  },
  mounted() {
    this.modalReady = true;
    this.isMounted = true;

    if (this.$route.hash && document.querySelector(this.$route.hash)) {
      document.querySelector(this.$route.hash).scrollIntoView();
    }
  },
  methods: {
    // used to fix a problem in firefox with the reorientation after the header is hidden
    scrollUp() {
      return window.scrollTo(0, 0);
    },
    visibilityChanged(isVisible, entry, objectNumber) {
      if (isVisible === true) {
        trackSearchObject(objectNumber);
      }
    },
  },
  i18nOptions: { namespaces: ['common', 'search'] },
};
</script>
<style lang="scss" src="@/scss/components/SearchObjectList.scss" scoped clientextend></style>
